import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { list } from "./service";
import { remove } from "./UpdateAttractionTabs/services/attractionService";
import AttractionSettings from "./AttractionSettings";
import Dialog from "../../dialog/Dialog";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  TableActionButton,
  TableActionsCell,
  DeleteActionButton,
} from "../../common/adminTable";
import { buildPaginationMeta } from "../../../utils/clientPagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";

import { useState, useCallback, useEffect } from "react";
import useTableSearch from "../../../hooks/useTableSearch";

/** Laravel casts `is_approved` as boolean → JSON `true`/`false`; UI must not use `=== 1` only. */
function attractionApprovalCode(isApproved) {
  if (isApproved === true || isApproved === 1 || isApproved === "1") return 1;
  if (isApproved === 2 || isApproved === "2") return 2;
  return 0;
}

const Attractions = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState(null);

  /* modal states */
  const [showStatus, setShowStatus] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attractionToDelete, setAttractionToDelete] = useState(null);

  const queryClient = useQueryClient();

  /* search and filter states */
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["attractions", currentPage, activeSearch],
    queryFn: () => list(currentPage, activeSearch),
    keepPreviousData: true,
  });

  const clearFilters = useCallback(() => {
    clearSearch();
    setCurrentPage(1);
  }, [clearSearch]);

  const deleteMutation = useMutation({
    mutationFn: remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attractions"] });
      setDeleteDialogOpen(false);
      setAttractionToDelete(null);
      setToast({ type: "success", message: "Attraction deleted successfully" });
    },
    onError: (err) => {
      setToast({
        type: "error",
        message: err?.response?.data?.message || "Failed to delete attraction",
      });
    },
  });

  const attractions = response?.data || [];
  const current_page = response?.current_page || 1;
  const last_page = response?.last_page || 1;
  const total = response?.total || 0;
  const per_page = response?.per_page || 10;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  const pageMeta = ADMIN_PAGE_META.attractions;

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin h-8 w-8 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="text-primary font-medium">
                Loading attractions...
              </span>
            </div>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="fi fi-rr-exclamation text-red-500 text-4xl mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading attractions
              </h3>
              <p className="text-gray-600">{error.message}</p>
            </div>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  const add = () => {
    navigate("/attractions/new", { state: { returnTo: "/attractions" } });
  };

  const openStatus = (attraction) => {
    setSelectedAttraction(attraction);
    setShowStatus(true);
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={pageMeta.title}
        subtitle={pageMeta.subtitle}
        actions={
          <button
            type="button"
            onClick={add}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
          >
            <i className="fi fi-rr-plus text-xs" />
            Add Attraction
          </button>
        }
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <TableSearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search attractions..."
            className="sm:w-[200px]"
          />
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination pagination={pagination} onPageChange={handlePageChange} />
            </AdminTableFooter>
          }
        >
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Attraction Name</AdminTableTh>
          <AdminTableTh>Supplier</AdminTableTh>
          <AdminTableTh>Category</AdminTableTh>
          <AdminTableTh>Location</AdminTableTh>
          <AdminTableTh>Timings</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {attractions.length === 0 ? (
            <TableEmptyRow
              colSpan={8}
              icon="fi fi-rr-mountain"
              emptyMessage="No attractions found"
              searchQuery={activeSearch}
              onClearSearch={clearFilters}
            />
          ) : (
            attractions.map((attraction, index) => (
              <AdminTableRow key={attraction.id}>
                <AdminTableCell>
                  {(current_page - 1) * 10 + index + 1}
                </AdminTableCell>
                <AdminTableCell className="font-medium">
                  {attraction.name ||
                    attraction.title ||
                    "Untitled Attraction"}
                </AdminTableCell>
                <AdminTableCell>
                  <div>
                    <p className="font-medium">{attraction.user?.name}</p>
                    <p className="text-xs text-gray-500">
                      {attraction.user?.phone}
                    </p>
                  </div>
                </AdminTableCell>
                <AdminTableCell>
                  {attraction.attraction_category_master?.name || "N/A"}
                </AdminTableCell>
                <AdminTableCell>
                  {attraction.location || attraction.address || "N/A"}
                </AdminTableCell>
                <AdminTableCell nowrap className="text-xs text-gray-600">
                  {(() => {
                    const formatTime = (time) => {
                      if (!time) return "N/A";
                      const [hours, minutes] = time.split(":");
                      const hour = parseInt(hours);
                      const ampm = hour >= 12 ? "PM" : "AM";
                      const hour12 = hour % 12 || 12;
                      return `${hour12
                        .toString()
                        .padStart(2, "0")}:${minutes} ${ampm}`;
                    };

                    if (!attraction.start_time || !attraction.end_time) {
                      return "N/A";
                    }

                    return `${formatTime(
                      attraction.start_time
                    )} - ${formatTime(attraction.end_time)}`;
                  })()}
                </AdminTableCell>
                <AdminTableCell>
                  <div className="flex flex-col gap-1">
                    {attractionApprovalCode(attraction.is_approved) === 1 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-600 mr-1.5"></span>
                        Approved
                      </span>
                    ) : attractionApprovalCode(attraction.is_approved) === 2 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 mr-1.5"></span>
                        Rejected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-600 mr-1.5"></span>
                        Pending
                      </span>
                    )}
                    {Number(attraction.popular) === 1 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-600">
                        Popular
                      </span>
                    )}
                    {Number(attraction.recommended) === 1 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
                        Recommended
                      </span>
                    )}
                  </div>
                </AdminTableCell>
                <TableActionsCell>
                  <TableActionButton
                    variant="edit"
                    showLabel={false}
                    onClick={() => {
                      navigate(`/attractions/${attraction.id}/edit`, {
                        state: { returnTo: "/attractions" },
                      });
                    }}
                  />
                  <TableActionButton
                    variant="settings"
                    showLabel={false}
                    onClick={() => openStatus(attraction)}
                  />
                  <DeleteActionButton
                    onClick={() => {
                      setAttractionToDelete(attraction);
                      setDeleteDialogOpen(true);
                    }}
                  />
                </TableActionsCell>
              </AdminTableRow>
            ))
          )}
        </AdminTableBody>
      </AdminTable>
      </AdminPageCard>

      {showStatus && (
        <AttractionSettings
          open={showStatus}
          onClose={() => {
            setShowStatus(false);
            setSelectedAttraction(null);
          }}
          attractionData={selectedAttraction}
          onNotify={setToast}
        />
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setAttractionToDelete(null);
        }}
        title={
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            Delete Attraction
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={() => deleteMutation.mutate(attractionToDelete?.id)}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setDeleteDialogOpen(false);
          setAttractionToDelete(null);
        }}
        isLoading={deleteMutation.isPending}
      >
        <p className="text-sm text-gray-500">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-gray-900">
            {attractionToDelete?.name || "this attraction"}
          </span>
          ? This action cannot be undone.
        </p>
      </Dialog>

      {toast && (
        <Toster
          message={toast.message}
          type={toast.type}
          duration={3000}
          onClose={() => setToast(null)}
        />
      )}
    </AdminPageLayout>
  );
};

export default Attractions;
